<template>
  <q-card flat bordered>
    <q-tabs
      v-model="tab"
      dense
      outside-arrows
      mobile-arrows
      :class="$q.dark.mode ? 'bg-grey-10' : 'text-grey-9 bg-grey-4'"
      active-color="white"
      :active-bg-color="$q.dark.mode ? 'grey-9' : 'blue-4'"
      :indicator-color="$q.dark.mode ? 'grey-7' : 'blue-5'"
      align="justify"
      narrow-indicator
    >
      <q-tab
        v-for="{ name, label, tooltip } in tabNames"
        :key="name"
        class="text-weight-light"
        :name="name"
        :label="label"
        no-caps
      >
        <q-tooltip>{{ tooltip }}</q-tooltip>
      </q-tab>
    </q-tabs>
    <q-separator />

    <q-tab-panels v-model="tab" animated>
      <q-tab-panel
        v-for="{ name, component } in tabNames"
        :key="name"
        :name="name"
        class="q-pa-none"
      >
        <component :is="component" :symbol="symbol" />
      </q-tab-panel>
    </q-tab-panels>
  </q-card>
</template>

<script setup>
import { ref, markRaw } from "vue";
import { useQuasar } from "quasar";

// Import Components
import StockWiseChartEssential from "src/components/StockWiseChartEssential.vue";
import StockWiseBrokerAnalytics from "src/components/StockProfile/StockWiseBrokerAnalytics.vue";
import StockWiseBrokerAnalyticsPro from "src/components/BrokerComponents/StockWiseBrokerAnalyticsPro.vue";

import StockBasicInfo from "src/components/StockBasicInfo.vue";
import PriceHistoryData from "src/components/PriceHistoryData.vue";
import StockWiseLevelAlert from "src/components/KeyValue/StockWiseLevelAlert.vue";
import StockWiseBulkTransaction from "src/components/StockProfile/StockWiseBulkTransaction.vue";
import AccumulationDistributionComponents from "src/components/AccumulationDistributionComponents/AccumulationDistributionComponents.vue";
import Broker from "src/components/StockProfile/StockProfileBroker.vue";
import CircuitRange from "src/components/StockProfile/CircuitRange/CircuitRange.vue";

import StockWiseFloorSheetV2 from "src/components/StockWiseFloorSheetV2.vue";

import StockSignals from "src/components/StockProfile/StockSignals/StockSignals.vue";
import StockMutualFund from "src/components/StockProfile/MutualFund/MutualFund.vue";
import StockFundamental from "src/components/StockProfile/StockFundamental/StockFundamental.vue";

// Define the props
const props = defineProps({
  symbol: {
    type: String,
    required: true,
  },
  isChartFooter: {
    type: Boolean,
    default: false,
  },
});

const $q = useQuasar();

const tab = ref("basic_info");

const tabNames = ref([
  {
    name: "basic_info",
    label: "Basic Info",
    tooltip: "Basic Info",
    component: markRaw(StockWiseChartEssential),
  },
  {
    name: "broker_analytics",
    label: "Broker",
    tooltip: "Broker",
    component: markRaw(StockWiseBrokerAnalytics),
  },
  {
    name: "broker_analytics_pro",
    label: "Broker Pro",
    tooltip: "Broker Pro",
    component: markRaw(StockWiseBrokerAnalyticsPro),
  },
  {
    name: "bulk_tnx",
    label: "Bulk Tnx",
    tooltip: "Bulk Tnx",
    component: markRaw(StockWiseBulkTransaction),
  },
  {
    name: "key_value",
    label: "Level Alert",
    tooltip: "Level Alert",
    component: markRaw(StockWiseLevelAlert),
  },
  {
    name: "floor_sheet",
    label: "Floorsheet",
    tooltip: "Floorsheet",
    component: markRaw(StockWiseFloorSheetV2),
  },
  {
    name: "accum_dist",
    label: "Acc/Dist",
    tooltip: "Accumulation/Distribution",
    component: markRaw(AccumulationDistributionComponents),
  },
]);

if (props.isChartFooter)
  tabNames.value.push(
    {
      name: "stock_signals",
      label: "Signals",
      tooltip: "Signals",
      component: markRaw(StockSignals),
    },
    {
      name: "circuit_range",
      label: "Circuit",
      tooltip: "Circuit Range",
      component: markRaw(CircuitRange),
    },

    {
      name: "fundamental",
      label: "Fundamental",
      tooltip: "Fundamental",
      component: markRaw(StockFundamental),
    },
    {
      name: "price_history",
      label: "Price Log",
      tooltip: "Price Log",
      component: markRaw(PriceHistoryData),
    },
    {
      name: "stock_mutual_fund",
      label: "Mutual Fund",
      tooltip: "Mutual Fund",
      component: markRaw(StockMutualFund),
    }
  );
</script>
